<template>
  <transition name="fade">
    <div
      class="modal-overlay"
      ref="overlay"
      v-show="visible"
      @click.self="handleOverlayClick"
    >
      <transition name="popup">
        <div
          class="message-content"
          :class="[type, messageClass]"
          v-if="visible"
        >
          <div v-if="messageIconPath" class="message-icon">
            <img :src="messageIconPath" :alt="type" />
          </div>
          <div class="main-message">{{ mainMessage }}</div>
          <div
            v-if="subMessage"
            class="sub-message"
            style="white-space: pre-line"
          >
            {{ subMessage }}
          </div>
          <div
            v-if="type === 'confirm' || showCancel || confirmOnly"
            class="message-btn-group"
          >
            <button
              v-if="!confirmOnly"
              class="btn cancel"
              @click="handleCancel"
            >
              {{ cancelText }}
            </button>
            <button class="btn confirm" @click="handleConfirm">
              {{ confirmText }}
            </button>
          </div>
          <div v-else class="message-btn-group">
            <button class="btn confirm" @click="handleCancel">關閉</button>
          </div>
        </div>
      </transition>
    </div>
  </transition>
</template>

<script>
export default {
  name: "ModalMessageBox",
  data() {
    return {
      messageIconPath: null,
      prevActiveEl: null,
    };
  },
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    type: {
      type: String,
      default: "alert",
      validator(value) {
        return ["alert", "error", "confirm", "success"].includes(value);
      },
    },
    mainMessage: {
      type: String,
      required: true,
    },
    subMessage: {
      type: String,
      default: "",
    },
    showCancel: {
      type: Boolean,
      default: false,
    },
    confirmOnly: {
      type: Boolean,
      default: false,
    },
    confirmText: {
      type: String,
      default: "確定",
    },
    cancelText: {
      type: String,
      default: "取消",
    },
    messageClass: {
      type: String,
      default: "",
    },
  },
  emits: ["confirm", "cancel"],
  watch: {
    visible(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          // 1) 記住原本焦點
          this.prevActiveEl = document.activeElement;
          // 2) 主動把背景焦點 blur 掉（重點）
          if (
            this.prevActiveEl &&
            typeof this.prevActiveEl.blur === "function"
          ) {
            this.prevActiveEl.blur();
          }
          // 3) 把焦點拉到彈窗 overlay（或你也可以改成 focus 到按鈕）
          this.$refs.overlay?.focus?.();
        });
      } else {
        // 關閉時把焦點還給原本元素（可選但建議）
        this.$nextTick(() => {
          this.prevActiveEl?.focus?.();
          this.prevActiveEl = null;
        });
      }
    },
    type: {
      handler(value) {
        if (value === "alert") {
          this.messageIconPath = this.$img.get("Message/alert.png");
        } else if (value === "error") {
          this.messageIconPath = this.$img.get("Message/error.png");
        } else if (value === "confirm") {
          this.messageIconPath = this.$img.get("Message/confirm.png");
        } else if (value === "success") {
          this.messageIconPath = this.$img.get("Message/success.png");
        }
      },
      immediate: true,
    },
  },
  methods: {
    handleConfirm() {
      this.$emit("confirm");
    },
    handleCancel() {
      this.$emit("cancel");
    },
    handleOverlayClick() {
      if (!this.confirmOnly) {
        this.handleCancel();
      }
    },
    handleGlobalEnter(e) {
      if (e.key === "Enter" && this.visible) {
        e.preventDefault(); // 阻止外部的 form submit
        this.handleConfirm(); // 呼叫本 modal 的確認邏輯
      }
    },
  },
  mounted() {
    document.addEventListener("keydown", this.handleGlobalEnter);
  },
  beforeUnmount() {
    document.removeEventListener("keydown", this.handleGlobalEnter);
  },
};
</script>

<style scoped>
/* Overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Modal content */
.message-content {
  position: relative;
  background: white;
  border-radius: 10px;
  padding: 1.5rem;
  min-width: 300px;
  max-width: 400px;
  max-height: 90vh;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  text-align: center;
  overflow: auto;
}
.message-icon {
  margin-bottom: 1rem;
}
/* Messages */
.main-message {
  font-size: 1.3rem;
  font-weight: bold;
  white-space: pre-line;
}

.sub-message {
  font-size: 1rem;
  margin-top: 0.5rem;
  color: #555;
}

/* Button group */
.message-btn-group {
  margin-top: 1.5rem;
  display: flex;
  justify-content: center;
  gap: 0.75rem;
}

.btn {
  cursor: pointer;
  min-width: 72px;
  height: 40px;
  padding: 0.4rem 1rem;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 400;
  border: none;
}

.btn.confirm {
  background-color: #4695c9;
  color: white;
}

.btn.cancel {
  border: 1px solid #4695c9;
  background-color: white;
  color: #4695c9;
}

.message-content.ip-allow-list-message {
  width: 300px;
  min-width: 300px;
  padding: 24px;
}

.ip-allow-list-message .main-message {
  font-size: 16px;
}

.ip-allow-list-message .message-btn-group {
  display: block;
  margin-top: 24px;
}

.ip-allow-list-message .btn.confirm {
  width: 100%;
}

/* Animations */

/* Fade-in animation */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Popup enter animation (scale from small to big) */
.popup-enter-active,
.popup-leave-active {
  transition: all 0.3s ease;
}
.popup-enter-from,
.popup-leave-to {
  transform: scale(0.8); /* start small */
  opacity: 0; /* invisible at the start */
}
.popup-enter-to,
.popup-leave-from {
  transform: scale(1); /* end normal size */
  opacity: 1; /* visible at the end */
}
</style>
